import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, CircleAlert, CircleCheck, MessageCircle, ShieldCheck, Timer } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { generateCode, login, fetchUserShops, clearError, pinStart, pinLogin, setPin, PinLoginError } from './authSlice';
import { Alert, Avatar, Button, IconButton, Input, NumericKeypad, OtpInput } from '@/components/ui';
import { Splash } from '@/components/layout/Splash';
import { validatePhone, validateAuthCode } from '@/utils/validators';
import { formatPhone } from '@/utils/formatters';
import { APP_VERSION, SUPPORT_WHATSAPP } from '@/utils/constants';
import { useI18n } from '@/hooks/useI18n.ts';

/**
 * 01 Masuk / 02 Verifikasi / 03 PIN — T1 brand split. Phone: plum hero + bottom form card.
 * Landscape: message column | 540px form card.
 * phone → pin (has PIN) | code → createPin → confirmPin (no PIN / forgot PIN).
 */
type Step = 'phone' | 'pin' | 'code' | 'createPin' | 'confirmPin';
type PinTone = 'default' | 'danger' | 'success';
const COUNTRY_CODES = ['+62', '+60', '+65'];
const PIN_LEN = 6;
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const isWeakPin = (p: string) => /^(\d)\1{5}$/.test(p) || '0123456789'.includes(p) || '9876543210'.includes(p);

const PinDots: React.FC<{ value: string; tone?: PinTone }> = ({ value, tone = 'default' }) => (
    <div className="flex justify-center gap-4" aria-label={`${value.length}/${PIN_LEN}`}>
        {Array.from({ length: PIN_LEN }).map((_, i) => (
            <span
                key={i}
                className={clsx(
                    'h-[18px] w-[18px] rounded-full transition-colors',
                    i < value.length
                        ? tone === 'danger' ? 'bg-danger-500' : tone === 'success' ? 'bg-success-500' : 'bg-ink-brand'
                        : 'border-2 border-line-default',
                )}
            />
        ))}
    </div>
);

const LoginPage: React.FC = () => {
    const { t, changeLanguage, isIndonesian } = useI18n();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, isLoading, error, isAuthenticated, isInitialized } = useAppSelector((state) => state.auth);

    const [step, setStep] = useState<Step>('phone');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+62');
    const [resendIn, setResendIn] = useState(0);
    const [authCode, setAuthCode] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [codeError, setCodeError] = useState('');
    const [fullname, setFullname] = useState('');
    const [pin, setPinDigits] = useState('');
    const [firstPin, setFirstPin] = useState('');
    const [pinTone, setPinTone] = useState<PinTone>('default');
    const [pinMsg, setPinMsg] = useState('');

    useEffect(() => {
        // Only the restored-session case: after an OTP login the create/confirm PIN steps must still show.
        if (isInitialized && isAuthenticated && step === 'phone') navigate('/shop-select');
    }, [isAuthenticated, isInitialized, navigate, step]);

    useEffect(() => {
        if (resendIn <= 0) return;
        const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [resendIn]);

    // Auto-submit once 6 digits are entered
    useEffect(() => {
        if (step === 'code' && authCode.length === 6 && !isLoading) submitLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCode]);

    useEffect(() => {
        if (pin.length !== PIN_LEN || isLoading) return;
        if (step === 'pin') submitPin();
        else if (step === 'createPin') {
            if (isWeakPin(pin)) { setPinTone('danger'); setPinDigits(''); return; }
            setFirstPin(pin);
            goPinStep('confirmPin');
        } else if (step === 'confirmPin') {
            if (pin === firstPin) { setPinTone('success'); setPinMsg(t('auth.pinMatch')); return; }
            setPinTone('danger'); setPinMsg(t('auth.pinMismatch'));
            const id = setTimeout(() => { setPinDigits(''); setPinTone('default'); setPinMsg(''); }, 700);
            return () => clearTimeout(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    if (!isInitialized) return <Splash />;

    const goPinStep = (s: Step) => { setStep(s); setPinDigits(''); setPinTone('default'); setPinMsg(''); };

    const enterShop = async (user: { usersId: number; fullname: string }) => {
        await dispatch(fetchUserShops(user.usersId)).unwrap();
        toast.success(t('auth.welcome', { name: user.fullname || 'User' }));
        navigate('/shop-select');
    };

    const handleGenerateCode = async (e?: React.FormEvent) => {
        e?.preventDefault();
        dispatch(clearError());
        try {
            await dispatch(generateCode({ phone, countryCode })).unwrap();
            toast.success(t('auth.codeSentToast'));
            setStep('code');
            setAuthCode('');
            setResendIn(60);
        } catch {
            toast.error(t('auth.sendCodeFailed'));
        }
    };

    const handlePinStart = async (e: React.FormEvent) => {
        e.preventDefault();
        setPhoneError('');
        if (!validatePhone(phone)) {
            setPhoneError('Invalid phone number (10-15 digits)');
            return;
        }
        dispatch(clearError());
        try {
            const res = await dispatch(pinStart({ phone, countryCode })).unwrap();
            setFullname(res.fullname ?? '');
            if (res.hasPin) { goPinStep('pin'); return; }
            toast.success(t('auth.codeSentToast'));
            setStep('code');
            setAuthCode('');
            setResendIn(60);
        } catch {
            toast.error(t('auth.sendCodeFailed'));
        }
    };

    const submitLogin = async () => {
        setCodeError('');
        if (!validateAuthCode(authCode)) {
            setCodeError('Invalid code (6 digits required)');
            return;
        }
        dispatch(clearError());
        try {
            const user = await dispatch(login({ phone, countryCode, authCode })).unwrap();
            setFullname(user.fullname ?? '');
            goPinStep('createPin');
        } catch {
            toast.error(t('auth.loginFailed'));
        }
    };

    const submitPin = async () => {
        try {
            const user = await dispatch(pinLogin({ phone, countryCode, pin })).unwrap();
            await enterShop(user);
        } catch (err) {
            const e = err as PinLoginError;
            setPinTone('danger');
            setPinDigits('');
            if (e.status === 423) setPinMsg(t('auth.pinLocked'));
            else if (e.status === 401) setPinMsg(t('auth.pinWrong', { n: e.remaining ?? 0 }));
            else setPinMsg(e.message);
        }
    };

    const submitSetPin = async () => {
        try {
            await dispatch(setPin(pin)).unwrap();
            if (user) await enterShop(user);
        } catch (e) {
            toast.error(typeof e === 'string' ? e : t('auth.loginFailed'));
        }
    };

    const handleBack = () => {
        setStep('phone');
        setAuthCode('');
        setCodeError('');
        setPinDigits('');
        dispatch(clearError());
    };

    const supportHref = `https://wa.me/${SUPPORT_WHATSAPP.number}?text=${encodeURIComponent(t('support.prefill'))}`;
    const languageOffer = !isIndonesian && (
        <button type="button" className="text-xs font-bold text-ink-accent" onClick={() => changeLanguage('id')}>🇮🇩 Bahasa Indonesia</button>
    );
    const firstName = (fullname || '').trim().split(/\s+/)[0] || 'Kasir';

    const phoneForm = (
        <form onSubmit={handlePinStart} className="flex flex-col gap-4">
            <h2 className="hidden text-[28px] font-black text-ink-brand lg:block">{t('auth.login')}</h2>
            {error && <Alert variant="error" message={error} onClose={() => dispatch(clearError())} />}
            <div className="flex flex-col gap-2">
                <label className="text-[13px] font-extrabold text-ink-secondary">{t('auth.phoneNumber')}</label>
                <div className="flex gap-2.5">
                    <label className="relative flex h-[58px] shrink-0 items-center rounded-md bg-surface-sunken px-3.5 text-lg font-extrabold lg:h-[60px] lg:px-4">
                        <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="appearance-none bg-transparent pr-6 text-lg font-extrabold outline-none tnum"
                            aria-label={t('auth.countryCode')}
                        >
                            {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-ink-muted" />
                    </label>
                    <div className="min-w-0 flex-1">
                    <Input
                        fieldSize="lg"
                        numeric
                        type="tel"
                        inputMode="numeric"
                        placeholder="812 3456 7890"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '')); setPhoneError(''); }}
                        error={phoneError}
                        required
                        autoFocus
                        wrapperClassName="!h-[58px] lg:!h-[60px]"
                    />
                    </div>
                </div>
                <p className="text-[13px] text-ink-muted">{t('auth.pinHelper')}</p>
            </div>
            <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoading}>{t('auth.next')}</Button>
            <Button variant="ghost" size="md" fullWidth icon={<MessageCircle />} onClick={() => window.open(supportHref, '_blank', 'noopener')}>{t('support.contact')}</Button>
            <div className="flex flex-col items-center gap-2">
                {languageOffer}
                <p className="text-xs text-ink-muted">{t('app.name')} v{APP_VERSION}</p>
            </div>
        </form>
    );

    const codeForm = (
        <form onSubmit={(e) => { e.preventDefault(); submitLogin(); }} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2.5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-success-soft text-success-700"><MessageCircle className="h-6 w-6" /></span>
                <h2 className="text-[32px] font-black leading-tight tracking-[-0.02em] text-ink-brand lg:text-[28px]">{t('auth.checkWhatsApp')}</h2>
                <p className="text-base text-ink-secondary lg:text-[15px]">
                    {t('auth.otpSentTo')} <strong className="font-extrabold text-ink-primary tnum">{formatPhone(phone, countryCode)}</strong>
                </p>
                <p className="text-[15px] font-bold text-ink-primary">{t('auth.verify')} · {t('auth.enterCode')}</p>
            </div>
            {error && <Alert variant="error" message={error} onClose={() => dispatch(clearError())} />}
            <OtpInput value={authCode} onChange={(v) => { setAuthCode(v); setCodeError(''); }} disabled={isLoading} autoFocus className="mt-2" />
            {codeError && <p className="text-xs font-semibold text-status-danger">{codeError}</p>}
            <button type="button" onClick={() => handleGenerateCode()} disabled={isLoading || resendIn > 0} className="flex items-center gap-2 text-sm text-ink-muted disabled:cursor-default">
                <Timer className="h-4 w-4" />
                {resendIn > 0 ? <span>{t('auth.resendIn', { time: mmss(resendIn) })}</span> : <span className="font-extrabold text-ink-accent">{t('auth.resendCode')}</span>}
            </button>
            <div className="flex-1" />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoading} disabled={authCode.length < 6}>{t('auth.verify')}</Button>
            <p className="text-center text-[13px] text-ink-secondary">
                {t('auth.wrongNumber')} <button type="button" onClick={handleBack} className="font-extrabold text-ink-accent">{t('auth.changeNumber')}</button>
            </p>
            <div className="flex justify-center">{languageOffer}</div>
        </form>
    );

    /* ---- PIN steps: side content (plum column on lg / top of card on phone) ---- */
    const isPinStep = step === 'pin' || step === 'createPin' || step === 'confirmPin';
    const pinSide = (onDark: boolean) => {
        const title = onDark ? 'text-white' : 'text-ink-brand';
        const body = onDark ? 'text-primary-100' : 'text-ink-secondary';
        if (step === 'pin') return (
            <div className={clsx('flex flex-col gap-3', onDark ? 'items-start' : 'items-center text-center')}>
                <Avatar name={fullname} className="!h-16 !w-16 !text-xl" />
                <h1 className={clsx('text-[28px] font-black leading-tight tracking-[-0.02em] lg:text-[40px]', title)}>{t('auth.greeting', { name: firstName })}</h1>
                <p className={clsx('text-base lg:text-[19px]', body)}>{t('auth.pinSide')}</p>
                <div className={clsx('flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-bold tnum', onDark ? 'bg-white/15 text-white' : 'bg-surface-sunken text-ink-primary')}>
                    {formatPhone(phone, countryCode)}
                    <button type="button" onClick={handleBack} className={clsx('font-extrabold', onDark ? 'text-primary-100 underline' : 'text-ink-accent')}>{t('auth.changeNumber')}</button>
                </div>
            </div>
        );
        const create = step === 'createPin';
        return (
            <div className={clsx('flex flex-col gap-3', onDark ? 'items-start' : 'items-center text-center')}>
                <span className={clsx('flex h-14 w-14 items-center justify-center rounded-full', onDark ? 'bg-white/15 text-white' : 'bg-status-success-soft text-success-700')}><ShieldCheck className="h-7 w-7" /></span>
                <h1 className={clsx('text-[24px] font-black leading-tight tracking-[-0.02em] lg:text-[40px]', title)}>{create ? t('auth.createPinTitleSide') : t('auth.confirmPin')}</h1>
                <p className={clsx('text-base lg:text-[19px]', body)}>{create ? t('auth.createPinSide') : t('auth.confirmPinSide')}</p>
                {create && (
                    <ul className={clsx('flex flex-col gap-2 text-left text-[15px]', body)}>
                        {(['pinRule1', 'pinRule2', 'pinRule3'] as const).map((k) => (
                            <li key={k} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success-500" />{t(`auth.${k}`)}</li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const pinPill = pinMsg && (
        <div className={clsx('flex items-center justify-center gap-2 self-center rounded-full px-3.5 py-2 text-sm font-bold',
            pinTone === 'success' ? 'bg-status-success-soft text-status-success' : 'bg-status-danger-soft text-status-danger')}>
            {pinTone === 'success' ? <CircleCheck className="h-4 w-4" /> : <CircleAlert className="h-4 w-4" />}
            {pinMsg}
        </div>
    );

    const pinForm = (
        <div className="flex flex-1 flex-col gap-5">
            {step !== 'pin' && (
                <div className="flex items-center gap-3">
                    <IconButton variant="card" label={t('common.back')} onClick={() => (step === 'createPin' ? handleBack() : goPinStep('createPin'))} className="lg:!bg-surface-sunken lg:!shadow-none"><ArrowLeft /></IconButton>
                    <span className="text-xs font-extrabold tracking-[.12em] text-ink-accent">{t('auth.stepOf', { step: step === 'createPin' ? 1 : 2 })}</span>
                </div>
            )}
            <div className="lg:hidden">{pinSide(false)}</div>
            <div className="flex flex-col items-center gap-1 text-center">
                <h2 className="text-[24px] font-black tracking-[-0.02em] text-ink-brand lg:text-[28px]">
                    {step === 'pin' ? t('auth.enterPin') : step === 'createPin' ? t('auth.createPin') : t('auth.confirmPin')}
                </h2>
                <p className="text-[15px] text-ink-secondary">
                    {step === 'pin' ? t('auth.pinSubtitle') : step === 'createPin' ? t('auth.createPinSubtitle') : t('auth.confirmPinSubtitle')}
                </p>
            </div>
            <PinDots value={pin} tone={pinTone} />
            {step === 'createPin'
                ? <p className={clsx('text-center text-[13px]', pinTone === 'danger' ? 'font-bold text-status-danger' : 'text-ink-muted')}>{t('auth.createPinHint')}</p>
                : pinPill}
            <NumericKeypad variant="pin" maxLength={PIN_LEN} value={pin} onChange={(v) => { setPinDigits(v); setPinTone('default'); setPinMsg(''); }} className="mt-auto !auto-rows-[64px] lg:mt-2" />
            {step === 'pin' && (
                <button type="button" disabled={isLoading} onClick={() => handleGenerateCode()} className="text-center text-[13px] text-ink-secondary">
                    {t('auth.forgotPin')} <span className="font-extrabold text-ink-accent">{t('auth.verifyByOtp')}</span>
                </button>
            )}
            {step === 'confirmPin' && (
                <Button variant="primary" size="lg" fullWidth loading={isLoading} disabled={pinTone !== 'success'} onClick={submitSetPin}>{t('auth.savePinLogin')}</Button>
            )}
        </div>
    );

    return (
        <div className={`flex min-h-dvh flex-col lg:flex-row ${step === 'phone' ? 'bg-surface-brand' : 'bg-surface-page lg:bg-surface-brand'}`}>
            {/* Hero / message column */}
            <div className={`flex flex-col items-center justify-center gap-[18px] px-8 text-center text-white lg:flex-1 lg:items-start lg:px-20 lg:text-left ${step === 'phone' ? 'flex-1 py-12' : 'hidden lg:flex'}`}>
                {isPinStep ? pinSide(true) : (
                    <>
                        <img src="/logo.svg" alt="" draggable={false} className="h-24 w-24 rounded-[24%] shadow-[0_12px_30px_rgba(0,0,0,.3)] lg:h-[104px] lg:w-[104px]" />
                        <h1 className="text-[32px] font-black leading-[1.1] tracking-[-0.02em] lg:text-[52px]">{t('auth.welcomeTitle')}</h1>
                        <p className="max-w-[420px] text-base text-primary-100 lg:text-[19px]">{t('auth.loginSubtitle')}</p>
                    </>
                )}
            </div>

            {/* Form card */}
            <div className={`flex flex-col lg:m-6 lg:w-[540px] lg:shrink-0 lg:justify-center lg:rounded-[28px] lg:bg-surface-card lg:p-12 lg:shadow-xl ${step === 'phone' ? 'rounded-[32px_32px_0_0] bg-surface-card p-[28px_24px_max(28px,env(safe-area-inset-bottom))]' : 'flex-1 px-6 pb-8 pt-6'}`}>
                {step === 'code' && (
                    <div className="mb-6 flex lg:mb-4">
                        <IconButton variant="card" label={t('common.back')} onClick={handleBack} className="lg:!bg-surface-sunken lg:!shadow-none"><ArrowLeft /></IconButton>
                    </div>
                )}
                {step === 'phone' ? phoneForm : step === 'code' ? codeForm : pinForm}
            </div>
        </div>
    );
};

export default LoginPage;
